import _ from "lodash";

import * as Api from "@modules/api";
import { db } from "@modules/local/db";
import { defaultPageSize } from "@modules/content/helpers/content";
import { uiEntitiesRepository } from "@modules/local/repositories/uiEntitiesRepository";
import { ApiMethod } from "@utils/types/common";
import { thunkErrorHandler } from "@utils/helpers/thunkErrorHandler";
import { thunkResponseCheckerSdk } from "@utils/helpers/thunkResponseCheckerSdk";
import {
  GetContentEntityItemsByIdInput,
  ItemContentOutputWithProgress,
  SearchTrackedInput,
} from "./types";

export const getContentEntityById: ApiMethod<
  string,
  Api.Server.ContentEntityOutput
> = async (contentId, { dispatch }) => {
  try {
    const client = new Api.Server.Server_ContentEntities_Client(
      undefined,
      Api.apiClient
    );

    const response = await client.contentEntities_byId_get_V1(
      undefined,
      contentId
    );

    if (response.success && response.data) {
      await db.entities.put(response.data, contentId);
      return response.data;
    } else {
      const errors = { ...response.errors };
      alert(JSON.stringify(errors));
      throw errors;
    }
  } catch (error: any) {
    console.log(error);
    console.log(error.response);
    const errors = { ...error.response }.data;
    alert(JSON.stringify(errors));
    throw errors;
  }
};

export const getContentEntityBySelectorsAndSorters: ApiMethod<
  {
    input: Api.Server.GetContentEntitiesBySelectorsAndSortersInput;
    uniqueId: string;
  },
  {
    data: Api.Server.PaginatedResultOfContentEntityOutput;
    uniqueId: string;
  }
> = async (variables, { dispatch }) => {
  try {
    const client = new Api.Server.Server_ContentEntities_Client(
      undefined,
      Api.apiClient
    );

    const response = await client.contentEntities_bySelectors_post_V1(
      variables.input
    );

    if (response.success && response.data) {
      // await db.componentEntities.where('componentUniqueId').equals(variables.uniqueId).delete();
      await Promise.all([
        db.entities.bulkPut(response.data),
        uiEntitiesRepository.put({
          uniqueId: variables.uniqueId,
          contentIds: response.data?.map((d) => d.id!) ?? [],
          hasNextPage: response.hasNextPage ?? false,
          pageNumber: response.currentPage ?? variables.input.pageNumber ?? 1,
          pageSize:
            response.pageSize ?? variables.input.pageSize ?? defaultPageSize,
          totalCount: response.totalCount ?? 0,
        }),
      ]);

      return {
        data: response,
        uniqueId: variables.uniqueId,
      };
    } else {
      const errors = { ...response.errors };
      alert(JSON.stringify(errors));
      throw errors;
    }
  } catch (error: any) {
    console.log(error);
    const errors = { ...error.response }.data;
    alert(JSON.stringify(errors));
    throw errors;
  }
};

export const getMoreContentEntityBySelectorsAndSorters: ApiMethod<
  {
    input: Omit<
      Omit<
        Api.Server.GetContentEntitiesBySelectorsAndSortersInput,
        "pageNumber"
      >,
      "pageSize"
    >;
    uniqueId: string;
  },
  {
    data: Api.Server.PaginatedResultOfContentEntityOutput;
    uniqueId: string;
  }
> = async (variables, { dispatch, getState }) => {
  try {
    const client = new Api.Server.Server_ContentEntities_Client(
      undefined,
      Api.apiClient
    );

    const uiInfo = await db.uiEntities.get(variables.uniqueId);

    // const state = getState() as RootState;

    const hasNextPage = uiInfo?.hasNextPage;
    const pageNumber = (uiInfo?.pageNumber ?? 1) + 1;
    const pageSize = uiInfo?.pageSize;

    if (!hasNextPage)
      return {
        data: {
          data: [],
          pageSize,
          pageNumber,
          hasNextPage,
        },
        uniqueId: variables.uniqueId,
      };

    const response = await client.contentEntities_bySelectors_post_V1({
      ...variables.input,
      pageNumber,
      pageSize,
    });

    if (response.success && response.data) {
      await Promise.all([
        db.entities.bulkPut(response.data),
        uiEntitiesRepository.put({
          uniqueId: variables.uniqueId,
          contentIds: [
            ...uiInfo.contentIds,
            ...(response.data?.map((d) => d.id!) ?? []),
          ],
          hasNextPage: response.hasNextPage ?? false,
          pageNumber: response.currentPage ?? pageNumber ?? 1,
          pageSize: response.pageSize ?? pageSize ?? defaultPageSize,
          totalCount: response.totalCount ?? 0,
        }),
      ]);

      return {
        data: response,
        uniqueId: variables.uniqueId,
      };
    } else {
      const errors = { ...response.errors };
      alert(JSON.stringify(errors));
      throw errors;
    }
  } catch (error: any) {
    console.log(error);
    const errors = { ...error.response }.data;
    alert(JSON.stringify(errors));
    throw errors;
  }
};

export const getContentEntitiesProgress: ApiMethod<
  string[],
  Api.Server.ContentEntityProgressOutput[]
> = async (variables, { dispatch, getState }) => {
  try {
    const client = new Api.Server.Server_Sessions_Client(
      undefined,
      Api.apiClient
    );

    const response = await client.sessions_myContentProgresses_post_V1({
      contentEntityIds: variables,
    });

    if (response.success && response.data) {
      await db.progresses.bulkPut(response.data);
      return response.data ?? [];
    } else {
      const errors = { ...response.errors };
      alert(JSON.stringify(errors));
      throw errors;
    }
  } catch (error: any) {
    console.log(error);
    const errors = { ...error.response }.data;
    alert(JSON.stringify(errors));
    throw errors;
  }
};

export const getContentEntityItemsById: ApiMethod<
  GetContentEntityItemsByIdInput,
  ItemContentOutputWithProgress[]
> = async (body, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Api.Server.Server_ContentEntities_Client(
        undefined,
        Api.apiClient
      );

      const response = await client.contentEntities_itemsById_get_V1(
        undefined,
        body.contentEntityId,
        body.narratorId
      );

      if (response.success && response.data) {
        const filteredData = response.data.filter(
          (d) => d.contentEntityType !== "BACKGROUNDMUSIC"
        ) as ItemContentOutputWithProgress[];

        const client = new Api.Server.Server_Sessions_Client(
          undefined,
          Api.apiClient
        );

        const progressResponse =
          await client.sessions_myContentProgresses_post_V1({
            contentEntityIds: filteredData.map((l) => l.id!),
          });

        if (progressResponse.success && progressResponse.data) {
          const progressDataObj = _.reduce(
            progressResponse.data,
            function (
              obj: { [key: string]: Api.Server.ContentEntityProgressOutput },
              param
            ) {
              if (param.contentEntityId) obj[param.contentEntityId] = param;

              return obj;
            },
            {}
          );

          for (const item of filteredData) {
            const progress = progressDataObj[item.id!];

            item.progress = progress?.currentProgress || 0;
            item.isCompleted = progress?.isCompleted;
          }

          return filteredData;
        }

        return rejectWithValue(progressResponse.errors);
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};

export const addEvent: ApiMethod<
  Api.Server.AddSessionEventInput,
  void,
  void
> = async (body, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(
        async () => {
          const client = new Api.Server.Server_Sessions_Client(
            undefined,
            Api.apiClient
          );

          return client.sessions_addEvent_post_V1(body);
        },
        rejectWithValue,
        false
      );
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const getInterests: ApiMethod<
  void,
  Api.Server.InterestOutput[],
  void
> = async (body, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(async () => {
        const client = new Api.Server.Server_Interests_Client(
          undefined,
          Api.apiClient
        );

        return client.interests_get_V1();
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const setInterests: ApiMethod<
  Api.Server.SetReaderInterestsInput,
  void,
  void
> = async (body, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(
        async () => {
          const client = new Api.Server.Server_Interests_Client(
            undefined,
            Api.apiClient
          );

          return client.interests_set_post_V1(body);
        },
        rejectWithValue,
        false
      );
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const addBookmark: ApiMethod<
  Api.Server.BookmarkContentEntityInput,
  void,
  void
> = async (body, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(
        async () => {
          const client = new Api.Server.Server_Bookmarks_Client(
            undefined,
            Api.apiClient
          );

          return client.bookmarks_add_post_V1(body);
        },
        rejectWithValue,
        false
      );
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const removeBookmark: ApiMethod<
  Api.Server.UnBookmarkContentEntityInput,
  void,
  void
> = async (body, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(
        async () => {
          const client = new Api.Server.Server_Bookmarks_Client(
            undefined,
            Api.apiClient
          );

          return client.bookmarks_remove_post_V1(body);
        },
        rejectWithValue,
        false
      );
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const getHighlights: ApiMethod<
  string | undefined,
  Api.Server.HighlightOutput[],
  void
> = async (id, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(async () => {
        const client = new Api.Server.Server_Highlights_Client(
          undefined,
          Api.apiClient
        );

        return client.highlights_byContainerId_get_V1("1", id);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const createHighlight: ApiMethod<
  Api.Server.CreateHighlightInput,
  void,
  void
> = async (body, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(
        async () => {
          const client = new Api.Server.Server_Highlights_Client(
            undefined,
            Api.apiClient
          );

          return client.highlights_post_V1(body);
        },
        rejectWithValue,
        false
      );
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const removeHighlight: ApiMethod<
  number | undefined,
  void,
  void
> = async (id, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(
        async () => {
          const client = new Api.Server.Server_Highlights_Client(
            undefined,
            Api.apiClient
          );

          return client.highlights_delete_V1(id);
        },
        rejectWithValue,
        false
      );
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const getDynamicLink: ApiMethod<
  string,
  Api.Server.GetContentDynamicLinkOutput,
  void
> = async (id, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(async () => {
        const client = new Api.Server.Server_DynamicLinks_Client(
          undefined,
          Api.apiClient
        );

        return client.dynamicLinks_content_ContentEntityId_get_V1(id);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const getNextContent: ApiMethod<
  Api.Server.GetNextContentEntityInput,
  Api.Server.ContentEntityOutput,
  void
> = async (body, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(async () => {
        const client = new Api.Server.Server_ContentEntities_Client(
          undefined,
          Api.apiClient
        );

        return client.contentEntities_next_post_V1(body);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const searchTracked: ApiMethod<
  SearchTrackedInput,
  {
    data: Api.Server.ContentEntityOutput[];
    meta: {
      hasNextPage: boolean | undefined;
      currentPage: number | undefined;
      totalCount: number | undefined;
    };
  },
  void
> = async (body, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(
        async () => {
          const { searchText, pageNumber, pageSize, typeName } = body;

          const client = new Api.Server.Server_ContentEntities_Client(
            undefined,
            Api.apiClient
          );

          return client.contentEntities_searchTracked_get_V1(
            undefined,
            searchText,
            typeName,
            undefined,
            pageNumber,
            pageSize
          );
        },
        rejectWithValue,
        true,
        true
      );
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};

export const getContentEntityItemsByPlaylistId: ApiMethod<
  number | undefined,
  Api.Server.PlaylistContentEntityOutput[]
> = async (id, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Api.Server.Server_ContentEntities_Client(
        undefined,
        Api.apiClient
      );

      const response = await client.contentEntities_byPlaylist_get_V1(
        undefined,
        id
      );

      if (response.success && response.data) {
        const client = new Api.Server.Server_Sessions_Client(
          undefined,
          Api.apiClient
        );

        const items = response.data as ItemContentOutputWithProgress[];

        const progressResponse =
          await client.sessions_myContentProgresses_post_V1({
            contentEntityIds: items.map((l) => l.id!),
          });

        if (progressResponse.success && progressResponse.data) {
          const progressDataObj = _.reduce(
            progressResponse.data,
            function (
              obj: { [key: string]: Api.Server.ContentEntityProgressOutput },
              param
            ) {
              if (param.contentEntityId) obj[param.contentEntityId] = param;

              return obj;
            },
            {}
          );

          for (const item of items) {
            const progress = progressDataObj[item.id!];

            item.progress = progress?.currentProgress || 0;
            item.isCompleted = progress?.isCompleted;
          }

          return items;
        }

        return rejectWithValue(progressResponse.errors);
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};

export const getContentEntityVoiceOvers: ApiMethod<
  string,
  Api.Server.ContentEntityVoiceOverOutput[]
> = async (id, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(async () => {
        const client = new Api.Server.Server_ContentEntityAudios_Client(
          undefined,
          Api.apiClient
        );

        return client.contentEntityAudios_voiceOvers_get_V1(undefined, id);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue,
    "page"
  );
};
